import { CheckCircle, Award, Users, Target } from 'lucide-react';

const AGENT_IMAGE = "https://customer-assets.emergentagent.com/job_green-properties-crt/artifacts/pflplf0p_immagine%20sito%202.jpg";
const OFFICE_IMAGE = "https://customer-assets.emergentagent.com/job_green-properties-crt/artifacts/43wt96my_sede.jpg";

const values = [
  { icon: Award, title: 'Professionalità', desc: 'Competenza e serietà in ogni trattativa' },
  { icon: Users, title: 'Fiducia', desc: 'Relazioni trasparenti con i nostri clienti' },
  { icon: Target, title: 'Risultati', desc: 'Obiettivi concreti e tempistiche rispettate' },
];

const timeline = [
  { year: '2011', title: 'La Fondazione', desc: 'Nasce Green House a Caserta con la passione per il settore immobiliare.' },
  { year: '2015', title: 'Crescita', desc: 'Ampliamento del team e dei servizi offerti ai nostri clienti.' },
  { year: '2020', title: 'Innovazione', desc: 'Introduzione di strumenti digitali per virtual tour e valutazioni online.' },
  { year: '2026', title: 'Oggi', desc: 'Oltre 15 anni di esperienza, leader sul territorio di Caserta con una solida reputazione.' },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fadeIn">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-accent mb-4">
              Chi Siamo
            </p>
            <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tight leading-[1.1] mb-6">
              La nostra storia, la tua casa
            </h1>
            <p className="font-sans text-lg text-white/80 leading-relaxed">
              Dal 2011 siamo al fianco delle famiglie di Caserta e provincia, 
              aiutandole a trovare la casa perfetta o a vendere il proprio immobile.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-background" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src={AGENT_IMAGE}
                alt="Agente Green House con clienti soddisfatti"
                className="rounded-xl shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">
                La Nostra Storia
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-text-main tracking-tight mb-6">
                Oltre 15 anni di esperienza
              </h2>
              <div className="space-y-4 font-sans text-text-muted leading-relaxed">
                <p>
                  Green House nasce nel 2011 dalla passione per il settore immobiliare e dalla volontà 
                  di offrire un servizio diverso: più umano, più attento, più professionale.
                </p>
                <p>
                  In questi anni abbiamo aiutato centinaia di famiglie a trovare la casa dei loro sogni 
                  o a vendere il proprio immobile al miglior prezzo di mercato.
                </p>
                <p>
                  La nostra sede si trova a Caserta, in Via Caduti sul Lavoro n.49, dove il nostro team 
                  è sempre pronto ad accoglierti per una consulenza personalizzata.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-background-alt" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">
              I Nostri Valori
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-text-main tracking-tight">
              Cosa ci guida ogni giorno
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-10 rounded-xl text-center animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon size={32} className="text-primary" />
                </div>
                <h3 className="font-serif text-2xl text-text-main mb-3">{value.title}</h3>
                <p className="font-sans text-text-muted">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-background" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">
              Il Nostro Percorso
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-text-main tracking-tight">
              Tappe importanti
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-primary/20 hidden md:block" />
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row items-center gap-8 animate-fadeIn ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white p-8 rounded-xl shadow-sm">
                      <span className="font-serif text-3xl text-primary font-semibold">{item.year}</span>
                      <h3 className="font-serif text-xl text-text-main mt-2">{item.title}</h3>
                      <p className="font-sans text-text-muted mt-2">{item.desc}</p>
                    </div>
                  </div>
                  <div className="relative z-10">
                    <div className="w-4 h-4 bg-primary rounded-full" />
                  </div>
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Office Section */}
      <section className="py-24 bg-background-alt" data-testid="office-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">
                La Nostra Sede
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-text-main tracking-tight mb-6">
                Vieni a trovarci
              </h2>
              <p className="font-sans text-text-muted leading-relaxed mb-8">
                Ti aspettiamo nella nostra sede di Caserta per una consulenza gratuita e senza impegno. 
                Il nostro team è pronto ad ascoltare le tue esigenze e a trovare insieme la soluzione migliore.
              </p>
              <div className="space-y-3">
                <p className="font-sans text-text-main">
                  <strong>Indirizzo:</strong> Via Caduti sul Lavoro n.49, Caserta
                </p>
                <p className="font-sans text-text-main">
                  <strong>Telefono:</strong> 0823 1848006
                </p>
                <p className="font-sans text-text-main">
                  <strong>Cellulare:</strong> +39 329 2435449
                </p>
                <p className="font-sans text-text-main">
                  <strong>Email:</strong> info@greenhousecaserta.it
                </p>
              </div>
            </div>
            <div>
              <img
                src={OFFICE_IMAGE}
                alt="Ufficio Green House"
                className="rounded-xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
